/**
 * VersionBadge — displays the pinned strategy version for a config/aggregation.
 *
 * Variants:
 * - Normal: renders "vN" in a subtle badge
 * - Stale: renders "vN" + warning indicator "⚠ vM available" when a newer version exists
 * - Unpinned: renders "unpinned" in muted style (legacy configs with no version)
 */

interface VersionBadgeProps {
  /** The pinned version number. null/undefined = legacy (unpinned). */
  strategyVersion?: number | null;
  /** The current (latest) version of the strategy. Used when isStale=true. */
  strategyCurrentVersion?: number | null;
  /** True when the pinned version is older than the current live version. */
  isStale?: boolean;
  /** Optional extra CSS class name */
  className?: string;
}

export function VersionBadge({
  strategyVersion,
  strategyCurrentVersion,
  isStale = false,
  className,
}: VersionBadgeProps) {
  const isUnpinned = strategyVersion == null;

  if (isUnpinned) {
    return (
      <span
        className={className}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '2px 7px',
          borderRadius: 4,
          fontSize: 11,
          fontWeight: 500,
          color: '#555',
          background: 'rgba(85,85,85,0.12)',
          border: '1px solid #333',
          letterSpacing: '0.02em',
        }}
      >
        unpinned
      </span>
    );
  }

  if (!isStale) {
    return (
      <span
        className={className}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '2px 7px',
          borderRadius: 4,
          fontSize: 11,
          fontWeight: 600,
          color: '#60a5fa',
          background: 'rgba(96,165,250,0.10)',
          border: '1px solid rgba(96,165,250,0.25)',
          letterSpacing: '0.02em',
        }}
      >
        v{strategyVersion}
      </span>
    );
  }

  // Stale: pinned version exists but is behind the current
  const tooltipText = `Strategy updated since this was pinned (pinned: v${strategyVersion}, current: v${strategyCurrentVersion ?? '?'})`;

  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
      }}
      title={tooltipText}
    >
      {/* Pinned version badge */}
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '2px 7px',
          borderRadius: 4,
          fontSize: 11,
          fontWeight: 600,
          color: '#facc15',
          background: 'rgba(250,204,21,0.10)',
          border: '1px solid rgba(250,204,21,0.30)',
          letterSpacing: '0.02em',
        }}
      >
        v{strategyVersion}
      </span>

      {/* Stale indicator */}
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 3,
          fontSize: 11,
          color: '#facc15',
          opacity: 0.85,
        }}
      >
        <span>⚠</span>
        {strategyCurrentVersion != null ? (
          <span>v{strategyCurrentVersion} available</span>
        ) : null}
      </span>
    </span>
  );
}
